import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { PremiseCommit } from "../context/PremiseWorkspace";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateKey, type Translate } from "../i18n/translate";
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
  type PremiseConfig,
  type ScheduleEntry,
} from "@shared/types/foyer";
import { activeScheduleFor, newScheduleId } from "@shared/schedule";
import { PageHeader } from "../components/PageHeader";
import { ItemFormSurface } from "../components/EditDrawer";

type RuleKind = "weekly" | "date-range" | "single";

interface Props {
  premise: PremiseConfig;
  /** Saves a change to the premise and toasts it (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}

/**
 * Per-premise scheduling rules - what page the TV considers "main" at
 * a given time/date. The TV evaluates rules locally every minute.
 *
 * Every change is saved the moment it is made and toasted: the Enabled switch
 * at once, a rule's form from its own Save, a delete after a confirmation.
 */
export default function SchedulePage({ premise, onCommit }: Props) {
  const { t } = useTranslation(["channels", "common"]);
  const schedules = useMemo(() => premise.schedules ?? [], [premise.schedules]);
  // `editing` is NOT cleared on close: the editor is always mounted, and
  // clearing it mid-slide would flip the drawer into the create modal. "New
  // rule" resets it on open instead.
  const [editing, setEditing] = useState<ScheduleEntry | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  // Re-render every minute so the "Active now" badge tracks reality.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const activeAll = useMemo(
    () =>
      activeScheduleFor(schedules, new Date(), null, premise.address?.timezone),
    [schedules, premise.address?.timezone]
  );

  const where = premise.premiseName;
  const label = (s: ScheduleEntry) => s.name || t("schedulePage.unnamed");

  /** Add or replace one rule, applied to the SAVED list. */
  function upsert(next: ScheduleEntry): Promise<boolean> {
    const adding = !schedules.some((s) => s.id === next.id);
    return onCommit(
      (c) => {
        const cur = c.schedules ?? [];
        return {
          ...c,
          schedules: cur.some((s) => s.id === next.id)
            ? cur.map((s) => (s.id === next.id ? next : s))
            : [...cur, next],
        };
      },
      {
        success: adding
          ? t("schedulePage.added", { name: label(next), where })
          : t("schedulePage.updated", { name: label(next) }),
        failure: adding ? "addScheduleRule" : "saveScheduleRule",
        failureParams: { name: label(next) },
      }
    );
  }

  function remove(s: ScheduleEntry) {
    if (!window.confirm(t("schedulePage.deleteConfirm", { name: label(s) })))
      return;
    void onCommit(
      (c) => ({
        ...c,
        schedules: (c.schedules ?? []).filter((x) => x.id !== s.id),
      }),
      {
        success: t("schedulePage.deleted", { name: label(s) }),
        failure: "deleteScheduleRule",
        failureParams: { name: label(s) },
      }
    );
  }

  function toggleEnabled(s: ScheduleEntry, enabled: boolean) {
    void onCommit(
      (c) => ({
        ...c,
        schedules: (c.schedules ?? []).map((x) =>
          x.id === s.id ? { ...x, enabled } : x
        ),
      }),
      {
        success: enabled
          ? t("schedulePage.turnedOn", { name: label(s) })
          : t("schedulePage.turnedOff", { name: label(s) }),
        failure: enabled ? "turnOnScheduleRule" : "turnOffScheduleRule",
        failureParams: { name: label(s) },
      }
    );
  }

  const pageById = new Map(premise.pages.map((p) => [p.id, p.name]));

  return (
    <Stack spacing={3} data-testid="schedule-page">
      <PageHeader
        eyebrow={t("schedulePage.eyebrow")}
        title={t("schedulePage.title")}
        help={t("schedulePage.help")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
            data-testid="schedule-new"
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {t("schedulePage.newRule")}
          </Button>
        }
      />

      <Paper variant="outlined">
        <Table size="small" data-testid="schedule-list">
          <TableHead>
            <TableRow>
              <TableCell>{t("common:labels.name")}</TableCell>
              <TableCell>{t("schedulePage.columns.page")}</TableCell>
              <TableCell>{t("schedulePage.columns.when")}</TableCell>
              <TableCell>{t("schedulePage.columns.scope")}</TableCell>
              <TableCell align="right">{t("common:labels.priority")}</TableCell>
              <TableCell align="center">{t("common:labels.enabled")}</TableCell>
              <TableCell sx={{ width: 96 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="caption" color="text.secondary">
                    {t("schedulePage.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {schedules.map((s) => {
              const isActive = activeAll?.id === s.id;
              return (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "fontWeightMedium" }}
                      >
                        {s.name || t("schedulePage.unnamed")}
                      </Typography>
                      {isActive && (
                        <Chip
                          size="small"
                          color="success"
                          label={t("schedulePage.activeNow")}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {pageById.get(s.pageId) ?? (
                        <span style={{ color: "rgba(0,0,0,0.4)" }}>
                          {t("schedulePage.deletedPage")}
                        </span>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {summariseWindow(t, s)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {s.scopeAllRooms
                        ? t("schedulePage.allUnits")
                        : t("schedulePage.unitCount", {
                            count: s.roomIds?.length ?? 0,
                          })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{s.priority}</TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={s.enabled}
                      onChange={(e) => toggleEnabled(s, e.target.checked)}
                      inputProps={
                        {
                          "data-testid": `schedule-toggle-${s.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={t("schedulePage.editRule")}>
                      <IconButton
                        size="small"
                        aria-label={t("schedulePage.editRuleAria")}
                        onClick={() => {
                          setEditing(s);
                          setEditorOpen(true);
                        }}
                        data-testid={`schedule-edit-${s.id}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("schedulePage.deleteRule")}>
                      <IconButton
                        size="small"
                        aria-label={t("schedulePage.deleteRuleAria")}
                        onClick={() => remove(s)}
                        data-testid={`schedule-delete-${s.id}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <ScheduleEditorDialog
        open={editorOpen}
        initial={editing}
        premise={premise}
        onClose={() => setEditorOpen(false)}
        // The editor slides out after submitting; its onClose then closes it.
        onSubmit={upsert}
      />
    </Stack>
  );
}

function dayLabel(t: Translate, d: DayOfWeek): string {
  return translateKey(t, `channels:schedulePage.days.${d}`);
}

function summariseWindow(t: Translate, s: ScheduleEntry): string {
  const k = (name: string, params?: Record<string, string>) =>
    translateKey(t, `channels:schedulePage.window.${name}`, params);
  const parts: string[] = [];
  if (s.startDate || s.endDate) {
    if (s.startDate && s.endDate && s.startDate === s.endDate) {
      parts.push(s.startDate);
    } else {
      parts.push(
        k("dateRange", { start: s.startDate ?? "-", end: s.endDate ?? "-" })
      );
    }
  }
  if (s.daysOfWeek && s.daysOfWeek.length > 0 && s.daysOfWeek.length < 7) {
    parts.push(s.daysOfWeek.map((d) => dayLabel(t, d)).join(", "));
  } else if (!s.startDate && !s.endDate) {
    parts.push(k("everyDay"));
  }
  if (s.startTime || s.endTime) {
    parts.push(
      k("timeRange", {
        start: s.startTime ?? "00:00",
        end: s.endTime ?? "24:00",
      })
    );
  } else {
    parts.push(k("allDay"));
  }
  return parts.join(" · ");
}

interface DialogProps {
  open: boolean;
  initial: ScheduleEntry | null;
  premise: PremiseConfig;
  onClose: () => void;
  /** Stores the rule; true = stored, and the editor then slides out. */
  onSubmit: (entry: ScheduleEntry) => Promise<boolean>;
}

function inferKind(s: ScheduleEntry | null): RuleKind {
  if (!s) return "weekly";
  if (s.startDate && s.endDate && s.startDate === s.endDate) return "single";
  if (s.startDate || s.endDate) return "date-range";
  return "weekly";
}

function ScheduleEditorDialog({
  open,
  initial,
  premise,
  onClose,
  onSubmit,
}: DialogProps) {
  const { t } = useTranslation(["channels", "common"]);
  const [name, setName] = useState("");
  const [pageId, setPageId] = useState("");
  const [kind, setKind] = useState<RuleKind>("weekly");
  const [days, setDays] = useState<DayOfWeek[]>([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [scopeAll, setScopeAll] = useState(true);
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [priority, setPriority] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name);
      setPageId(initial.pageId);
      setKind(inferKind(initial));
      setDays(initial.daysOfWeek ?? []);
      setStartTime(initial.startTime ?? "");
      setEndTime(initial.endTime ?? "");
      setStartDate(initial.startDate ?? "");
      setEndDate(initial.endDate ?? "");
      setScopeAll(initial.scopeAllRooms);
      setRoomIds(initial.roomIds ?? []);
      setPriority(initial.priority ?? 0);
      setEnabled(initial.enabled);
    } else {
      setName("");
      setPageId(
        premise.pages.find((p) => p.isMain)?.id ?? premise.pages[0]?.id ?? ""
      );
      setKind("weekly");
      setDays([]);
      setStartTime("");
      setEndTime("");
      setStartDate("");
      setEndDate("");
      setScopeAll(true);
      setRoomIds([]);
      setPriority(0);
      setEnabled(true);
    }
  }, [open, initial, premise.pages]);

  function toggleDay(d: DayOfWeek) {
    setDays((cur) =>
      cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]
    );
  }

  async function submit(dismiss: () => void) {
    const entry: ScheduleEntry = {
      id: initial?.id ?? newScheduleId(),
      name: name.trim() || t("schedulePage.untitledRule"),
      pageId,
      scopeAllRooms: scopeAll,
      roomIds: scopeAll ? undefined : roomIds,
      daysOfWeek:
        kind === "weekly" && days.length > 0 && days.length < 7
          ? days
          : undefined,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      startDate:
        kind === "single"
          ? startDate || undefined
          : kind === "date-range"
            ? startDate || undefined
            : undefined,
      endDate:
        kind === "single"
          ? startDate || undefined
          : kind === "date-range"
            ? endDate || undefined
            : undefined,
      priority,
      enabled,
    };
    setSaving(true);
    const ok = await onSubmit(entry);
    setSaving(false);
    if (ok) dismiss();
  }

  const canSave =
    !!pageId &&
    (scopeAll || roomIds.length > 0) &&
    (kind !== "single" || !!startDate) &&
    (kind !== "date-range" || (!!startDate && !!endDate));

  const missingDate =
    (kind === "single" && !startDate) ||
    (kind === "date-range" && (!startDate || !endDate));
  const missingUnit = !scopeAll && roomIds.length === 0;
  const needKey = missingDate
    ? kind === "single"
      ? missingUnit
        ? "needPageDateAndUnit"
        : "needPageAndDate"
      : missingUnit
        ? "needPageRangeAndUnit"
        : "needPageAndRange"
    : missingUnit
      ? "needPageAndUnit"
      : "needPage";

  return (
    <ItemFormSurface
      mode={initial ? "edit" : "create"}
      open={open}
      onClose={onClose}
      title={
        initial
          ? t("schedulePage.editor.editTitle")
          : t("schedulePage.editor.newTitle")
      }
      busy={saving}
      maxWidth="sm"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={saving}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!canSave || saving}
            onClick={() => void submit(() => dismiss())}
            data-testid="sched-save"
          >
            {saving
              ? t("common:states.saving")
              : initial
                ? t("common:buttons.save")
                : t("schedulePage.editor.addRule")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label={t("schedulePage.editor.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          autoFocus
          inputProps={{ "data-testid": "sched-name" }}
        />
        <TextField
          select
          label={t("schedulePage.editor.showPage")}
          value={pageId}
          onChange={(e) => setPageId(e.target.value)}
          size="small"
          inputProps={{ "data-testid": "sched-page" }}
        >
          {premise.pages.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.isMain
                ? t("schedulePage.editor.mainSuffix", { name: p.name })
                : p.name}
            </MenuItem>
          ))}
        </TextField>

        <Tabs
          value={kind}
          onChange={(_e, v) => setKind(v as RuleKind)}
          variant="fullWidth"
        >
          <Tab label={t("schedulePage.editor.kindWeekly")} value="weekly" />
          <Tab
            label={t("schedulePage.editor.kindDateRange")}
            value="date-range"
          />
          <Tab label={t("schedulePage.editor.kindSingle")} value="single" />
        </Tabs>

        {kind === "weekly" && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("schedulePage.editor.daysHint")}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
              {DAYS_OF_WEEK.map((d) => (
                <Chip
                  key={d}
                  label={dayLabel(t, d)}
                  onClick={() => toggleDay(d)}
                  color={days.includes(d) ? "primary" : "default"}
                  variant={days.includes(d) ? "filled" : "outlined"}
                  size="small"
                  data-testid={`sched-day-${d}`}
                />
              ))}
            </Stack>
          </Box>
        )}

        {kind === "date-range" && (
          <Stack direction="row" spacing={2}>
            <TextField
              type="date"
              label={t("schedulePage.editor.start")}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-testid": "sched-start-date" }}
            />
            <TextField
              type="date"
              label={t("schedulePage.editor.end")}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-testid": "sched-end-date" }}
            />
          </Stack>
        )}

        {kind === "single" && (
          <TextField
            type="date"
            label={t("schedulePage.editor.date")}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-testid": "sched-single-date" }}
          />
        )}

        <Stack direction="row" spacing={2}>
          <TextField
            label={t("schedulePage.editor.fromTime")}
            placeholder={t("schedulePage.editor.timePlaceholder")}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            size="small"
            helperText={t("schedulePage.editor.fromHint")}
            inputProps={{
              pattern: "([01][0-9]|2[0-3]):[0-5][0-9]",
              maxLength: 5,
              "data-testid": "sched-start-time",
            }}
          />
          <TextField
            label={t("schedulePage.editor.toTime")}
            placeholder={t("schedulePage.editor.timePlaceholder")}
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            size="small"
            helperText={t("schedulePage.editor.toHint")}
            inputProps={{
              pattern: "([01][0-9]|2[0-3]):[0-5][0-9]",
              maxLength: 5,
              "data-testid": "sched-end-time",
            }}
          />
        </Stack>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={scopeAll}
                onChange={(e) => setScopeAll(e.target.checked)}
                inputProps={
                  {
                    "data-testid": "sched-scope-all",
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={t("schedulePage.editor.applyToEveryUnit")}
          />
          {!scopeAll && (
            <TextField
              select
              fullWidth
              size="small"
              label={t("schedulePage.editor.units")}
              value={roomIds}
              onChange={(e) =>
                setRoomIds(
                  typeof e.target.value === "string"
                    ? e.target.value.split(",")
                    : (e.target.value as string[])
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: (v) => {
                  const ids = v as string[];
                  return ids.length === 0
                    ? t("schedulePage.editor.noneSelected")
                    : ids.join(", ");
                },
              }}
              helperText={t("schedulePage.editor.unitsSelected", {
                selected: roomIds.length,
                count: premise.rooms.length,
              })}
              inputProps={{ "data-testid": "sched-rooms" }}
            >
              {premise.rooms.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name
                    ? t("schedulePage.editor.roomWithName", {
                        id: r.id,
                        name: r.name,
                      })
                    : r.id}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="number"
            label={t("schedulePage.editor.priority")}
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value) || 0)}
            size="small"
            helperText={t("schedulePage.editor.priorityHint")}
            sx={{ width: 180 }}
            inputProps={{ "data-testid": "sched-priority" }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label={t("schedulePage.editor.enabled")}
          />
        </Stack>

        {!canSave && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t(`schedulePage.editor.${needKey}`)}
          </Alert>
        )}
      </Stack>
    </ItemFormSurface>
  );
}
